import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Box, CssBaseline, useTheme, useMediaQuery, ThemeProvider } from '@mui/material';
import { Header } from '../components/Header';
import NavigationDrawer from '../components/NavigationDrawer';
import { HomePage } from '../pages/HomePage';
import { ProfilePage } from '../pages/ProfilePage';
import { SettingsPage } from '../pages/SettingsPage';
import DataAccessRequestPage from '../pages/DataAccessRequestPage';
import { AuthPage } from '../pages/AuthPage';
import { PublicHomePage } from '../pages/PublicHomePage';
import SearchCaretakersPage from '../pages/SearchCaretakersPage';
import SearchPatientsPage from '../pages/SearchPatientsPage';
import PatientManagementPage from '../pages/PatientManagementPage';
import CaretakerProfilePage from '../pages/CaretakerProfilePage';
import MyCarePage from '../pages/MyCarePage';
import MyPatientsPage from '../pages/MyPatientsPage';
import AppointmentsDashboardPage from '../pages/AppointmentsDashboardPage';
import { PasswordTestForm } from '../components/PasswordTestForm';
import TrainingDashboardPage from '../pages/TrainingDashboardPage';
import TrainingCoursePage from '../pages/TrainingCoursePage';
import TrainingCertificatesPage from '../pages/TrainingCertificatesPage';
import MessagesPage from '../pages/MessagesPage';
import NotificationsPage from '../pages/NotificationsPage';
// FEAT-057 — Save / Favorite Caregivers list page (MVP-18.29).
import FavoritesPage from '../pages/FavoritesPage';
// FEAT-056 — Customizable Care Reports builder page (MVP-18.34).
import CareReportsPage from '../pages/CareReportsPage';
// FEAT-045 — Caregiver Sign-Up Stepper page (MVP-8.10, MVP-8.11, MVP-18.17).
import CaregiverSignUpPage from '../pages/CaregiverSignUpPage';
import CaregiverProfileManagementPage from '../pages/CaregiverProfileManagementPage';
// FEAT-061 — Caregiver Trust & Safety onboarding (MVP-8.20…8.27).
import CaregiverTrustSafetyPage from '../pages/CaregiverTrustSafetyPage';
// FEAT-064 — Caregiver Service Areas (MVP-18.33).
import CaregiverServiceAreasPage from '../pages/CaregiverServiceAreasPage';
import CareseekerLeadsPage from '../pages/caregiver/CareseekerLeadsPage';
import PremiumPlacementPage from '../pages/caregiver/PremiumPlacementPage';
// FEAT-055 — Client shift-report review page (MVP-18.61, 18.62, 18.64).
import ShiftReportReviewPage from '../pages/ShiftReportReviewPage';
import { ErrorBoundary } from '../components/ErrorBoundary';

// Footer pages - For Patients
import { FindCaregiversPage } from '../pages/footer/FindCaregiversPage';
import { HowItWorksPage } from '../pages/footer/HowItWorksPage';
import { SafetyPage } from '../pages/footer/SafetyPage';
import { InsurancePage } from '../pages/footer/InsurancePage';
import { PatientStoriesPage } from '../pages/footer/PatientStoriesPage';

// Footer pages - For Caregivers
import { BecomeCaregiverPage } from '../pages/footer/BecomeCaregiverPage';
import { CaregiverResourcesPage } from '../pages/footer/CaregiverResourcesPage';
import { EarningsPage } from '../pages/footer/EarningsPage';
import { BackgroundCheckPage } from '../pages/footer/BackgroundCheckPage';
import { CaregiverStoriesPage } from '../pages/footer/CaregiverStoriesPage';

// Footer pages - Company
import { AboutPage } from '../pages/footer/AboutPage';
import { MissionPage } from '../pages/footer/MissionPage';
import { CareersPage } from '../pages/footer/CareersPage';
import { PressPage } from '../pages/footer/PressPage';
import { ContactPage } from '../pages/footer/ContactPage';

// Footer pages - Support
import { HelpPage } from '../pages/footer/HelpPage';
import { SafetyGuidelinesPage } from '../pages/footer/SafetyGuidelinesPage';
import { CommunityGuidelinesPage } from '../pages/footer/CommunityGuidelinesPage';
import { ReportPage } from '../pages/footer/ReportPage';
import { AccessibilityPage } from '../pages/footer/AccessibilityPage';

// Footer pages - Legal
import { TermsPage } from '../pages/footer/TermsPage';
import { PrivacyPage } from '../pages/footer/PrivacyPage';
import { CookiesPage } from '../pages/footer/CookiesPage';
import { HipaaPage } from '../pages/footer/HipaaPage';
import { useApp } from '../context/AppContext';
import { authService } from '../services/authService';
import { publicTheme } from '../theme/publicTheme';
import { getDashboardRoute } from '../utils/navigation';
import { ScrollToTop } from '../components/ScrollToTop';

// Protected route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { state, dispatch } = useApp();
  const location = useLocation();

  const isAuthenticated = authService.isAuthenticated() && !!state.user;
  const redirectUrl = location.pathname + location.search;

  useEffect(() => {
    if (!isAuthenticated) {
      // Store current location for post-auth redirect.
      dispatch({ type: 'SET_REDIRECT_URL', payload: redirectUrl });
    }
  }, [dispatch, isAuthenticated, redirectUrl]);

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  return <>{children}</>;
};

// Role-based route component
const RoleBasedRoute: React.FC<{ 
  children: React.ReactNode; 
  allowedRoles: ('PATIENT' | 'CAREGIVER' | 'ADMIN')[];
  fallbackPath?: string;
}> = ({ children, allowedRoles, fallbackPath = '/' }) => {
  const { state, dispatch } = useApp();
  const location = useLocation();
  const user = state.user;

  const isAuthenticated = authService.isAuthenticated() && !!user;
  const redirectUrl = location.pathname + location.search;

  useEffect(() => {
    if (!isAuthenticated) {
      // Store current location for post-auth redirect.
      dispatch({ type: 'SET_REDIRECT_URL', payload: redirectUrl });
    }
  }, [dispatch, isAuthenticated, redirectUrl]);

  if (!isAuthenticated || !user) {
    return <Navigate to="/auth" replace />;
  }
  
  if (user.role && !allowedRoles.includes(user.role)) {
    return <Navigate to={fallbackPath} replace />;
  }
  
  return <>{children}</>;
};

// Public route component (redirects to appropriate dashboard if authenticated)
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { state } = useApp();
  
  if (authService.isAuthenticated() && state.user) {
    const dashboardRoute = getDashboardRoute(state.user);
    return <Navigate to={dashboardRoute} replace />;
  }
  
  return <>{children}</>;
};

// Theme wrapper component that switches themes based on route
const ThemeWrapper: React.FC<{ children: React.ReactNode; theme: any }> = ({ children, theme }) => {
  return (
    <ThemeProvider theme={theme}>
      {children}
    </ThemeProvider>
  );
};

// Authenticated app component for protected routes
const AuthenticatedApp: React.FC<{
  drawerOpen: boolean;
  onMenuClick: () => void;
  onDrawerClose: () => void;
}> = ({ drawerOpen, onMenuClick, onDrawerClose }) => {
  const { state } = useApp();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Navigation Drawer - only for authenticated users */}
      {authService.isAuthenticated() && state.user && (
        <NavigationDrawer
          open={drawerOpen}
          onClose={onDrawerClose}
        />
      )}
      
      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        {/* Header - only for authenticated users */}
        {authService.isAuthenticated() && state.user && (
          <Header
            onMenuClick={onMenuClick}
            title="Careva"
          />
        )}
        
        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1, 
            p: authService.isAuthenticated() && state.user ? 3 : 0,
          }}
        >
          <Routes>
            {/* Public routes */}
            <Route 
              path="/auth" 
              element={
                <PublicRoute>
                  <AuthPage />
                </PublicRoute>
              } 
            />
            
            {/* Protected routes */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <HomePage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/settings" 
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/settings/data-access"
              element={
                <ProtectedRoute>
                  <DataAccessRequestPage />
                </ProtectedRoute>
              }
            />
            
            {/* Search routes - available to all authenticated users */}
            <Route 
              path="/search" 
              element={
                <ProtectedRoute>
                  <SearchCaretakersPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/search-caretakers" 
              element={
                <ProtectedRoute>
                  <SearchCaretakersPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/caretaker/:id" 
              element={
                <ProtectedRoute>
                  <CaretakerProfilePage />
                </ProtectedRoute>
              } 
            />

            {/* FEAT-057 — Save / Favorite Caregivers list (MVP-18.29). Auth-gated;
                wrapped in a route-level error boundary per the web pattern. */}
            <Route
              path="/favorites"
              element={
                <ProtectedRoute>
                  <ErrorBoundary fallbackTitle="Unable to load your saved caregivers">
                    <FavoritesPage />
                  </ErrorBoundary>
                </ProtectedRoute>
              }
            />
            {/* FEAT-056 — Customizable Care Reports builder (MVP-18.34).
                Available to both careseekers and caregivers (and admins);
                wrapped in a route-level error boundary per the web pattern. */}
            <Route
              path="/care-reports"
              element={
                <ProtectedRoute>
                  <ErrorBoundary fallbackTitle="Unable to load Care Reports">
                    <CareReportsPage />
                  </ErrorBoundary>
                </ProtectedRoute>
              }
            />
            <Route 
              path="/search-patients" 
              element={
                <RoleBasedRoute allowedRoles={['CAREGIVER', 'ADMIN']}>
                  <SearchPatientsPage />
                </RoleBasedRoute>
              } 
            />
            
            {/* Role-based routes */}
            <Route 
              path="/patient-dashboard" 
              element={
                <RoleBasedRoute allowedRoles={['PATIENT']}>
                  <HomePage />
                </RoleBasedRoute>
              } 
            />
            <Route 
              path="/caregiver-dashboard" 
              element={
                <RoleBasedRoute allowedRoles={['CAREGIVER', 'ADMIN']}>
                  <HomePage />
                </RoleBasedRoute>
              } 
            />
            
            {/* Appointments - available to both patients and caregivers */}
            <Route 
              path="/appointments" 
              element={
                <ProtectedRoute>
                  <AppointmentsDashboardPage />
                </ProtectedRoute>
              } 
            />
            
            {/* Messages - available to both patients and caregivers */}
            <Route 
              path="/messages" 
              element={
                <ProtectedRoute>
                  <MessagesPage />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <NotificationsPage />
                </ProtectedRoute>
              }
            />
            
            {/* Patient-specific routes */}
            <Route 
              path="/my-care" 
              element={
                <RoleBasedRoute allowedRoles={['PATIENT']}>
                  <MyCarePage />
                </RoleBasedRoute>
              } 
            />
            
            {/* Caregiver-specific routes */}
            <Route 
              path="/my-patients" 
              element={
                <RoleBasedRoute allowedRoles={['CAREGIVER', 'ADMIN']}>
                  <MyPatientsPage />
                </RoleBasedRoute>
              } 
            />
            <Route 
              path="/patient-management" 
              element={
                <RoleBasedRoute allowedRoles={['CAREGIVER', 'ADMIN']}>
                  <PatientManagementPage />
                </RoleBasedRoute>
              } 
            />
            
            {/* Training routes - available to caregivers */}
            <Route 
              path="/training" 
              element={
                <RoleBasedRoute allowedRoles={['CAREGIVER', 'ADMIN']}>
                  <TrainingDashboardPage />
                </RoleBasedRoute>
              } 
            />
            <Route 
              path="/training/courses/:courseId" 
              element={
                <RoleBasedRoute allowedRoles={['CAREGIVER', 'ADMIN']}>
                  <TrainingCoursePage />
                </RoleBasedRoute>
              } 
            />
            <Route 
              path="/training/certificates" 
              element={
                <RoleBasedRoute allowedRoles={['CAREGIVER', 'ADMIN']}>
                  <TrainingCertificatesPage />
                </RoleBasedRoute>
              } 
            />
            
            {/* FEAT-045 — Caregiver Sign-Up Stepper + management surface
                (MVP-8.10, MVP-8.11, MVP-18.17). The stepper writes to
                CaregiverOnboardingProgress; the management page renders
                the "Continue sign-up" banner when isComplete is false. */}
            <Route
              path="/caregiver-sign-up"
              element={
                <RoleBasedRoute allowedRoles={['CAREGIVER', 'ADMIN']}>
                  <CaregiverSignUpPage />
                </RoleBasedRoute>
              }
            />
            <Route
              path="/caregiver-profile-management"
              element={
                <RoleBasedRoute allowedRoles={['CAREGIVER', 'ADMIN']}>
                  <CaregiverProfileManagementPage />
                </RoleBasedRoute>
              }
            />

            {/* FEAT-061 — Caregiver Trust & Safety onboarding step
                (MVP-8.20…8.27). A required gate that presents the eight §8
                trust-and-safety basics and records the caregiver's explicit
                acknowledgement. Wrapped in a route-level error boundary. */}
            <Route
              path="/caregiver-trust-safety"
              element={
                <RoleBasedRoute allowedRoles={['CAREGIVER', 'ADMIN']}>
                  <ErrorBoundary fallbackTitle="Unable to load trust & safety basics">
                    <CaregiverTrustSafetyPage />
                  </ErrorBoundary>
                </RoleBasedRoute>
              }
            />

            {/* FEAT-064 — Caregiver Service Areas (MVP-18.33). Caregivers
                select the zip-code areas they serve, gated behind an
                explicit confirmation agreement. Wrapped in an error boundary. */}
            <Route
              path="/caregiver/service-areas"
              element={
                <RoleBasedRoute allowedRoles={['CAREGIVER', 'ADMIN']}>
                  <ErrorBoundary fallbackTitle="Unable to load service areas">
                    <CaregiverServiceAreasPage />
                  </ErrorBoundary>
                </RoleBasedRoute>
              }
            />

            {/* FEAT-069 — Curated careseeker leads (MVP-18.11). */}
            <Route
              path="/caregiver/careseeker-leads"
              element={
                <RoleBasedRoute allowedRoles={['CAREGIVER', 'ADMIN']}>
                  <ErrorBoundary fallbackTitle="Unable to load careseeker leads">
                    <CareseekerLeadsPage />
                  </ErrorBoundary>
                </RoleBasedRoute>
              }
            />

            {/* FEAT-065 — Premium area placement (MVP-18.7). */}
            <Route
              path="/caregiver/premium-placement"
              element={
                <RoleBasedRoute allowedRoles={['CAREGIVER', 'ADMIN']}>
                  <ErrorBoundary fallbackTitle="Unable to load premium placement">
                    <PremiumPlacementPage />
                  </ErrorBoundary>
                </RoleBasedRoute>
              }
            />

            {/* FEAT-055 — Client (careseeker) shift-report review + client-initiated
                payment (MVP-18.61, 18.62, 18.64). Admins can also review. Wrapped
                in a route-level error boundary. */}
            <Route
              path="/shift-reports/review"
              element={
                <RoleBasedRoute allowedRoles={['PATIENT', 'ADMIN']}>
                  <ErrorBoundary fallbackTitle="Unable to load this shift report">
                    <ShiftReportReviewPage />
                  </ErrorBoundary>
                </RoleBasedRoute>
              }
            />

            <Route 
              path="/password-test" 
              element={<PasswordTestForm />} 
            />
            
            {/* Footer pages - For Patients */}
            <Route path="/find-caregivers" element={<FindCaregiversPage />} />
            <Route path="/how-it-works" element={<HowItWorksPage />} />
            <Route path="/safety" element={<SafetyPage />} />
            <Route path="/insurance" element={<InsurancePage />} />
            <Route path="/stories" element={<PatientStoriesPage />} />
            
            {/* Footer pages - For Caregivers */}
            <Route path="/caregiver-resources" element={<CaregiverResourcesPage />} />
            <Route path="/earnings" element={<EarningsPage />} />
            <Route path="/background-check" element={<BackgroundCheckPage />} />
            <Route path="/caregiver-stories" element={<CaregiverStoriesPage />} />
            
            {/* Footer pages - Company */}
            <Route path="/about" element={<AboutPage />} />
            <Route path="/mission" element={<MissionPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/press" element={<PressPage />} />
            <Route path="/contact" element={<ContactPage />} />
            
            {/* Footer pages - Support */}
            <Route path="/help" element={<HelpPage />} />
            <Route path="/safety-guidelines" element={<SafetyGuidelinesPage />} />
            <Route path="/community-guidelines" element={<CommunityGuidelinesPage />} />
            <Route path="/report" element={<ReportPage />} />
            <Route path="/accessibility" element={<AccessibilityPage />} />
            
            {/* Footer pages - Legal */}
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route path="/hipaa" element={<HipaaPage />} />
            
            {/* Default redirect - redirect to appropriate dashboard based on role */}
            <Route path="*" element={
              <ProtectedRoute>
                <Navigate to={getDashboardRoute(state.user)} replace />
              </ProtectedRoute>
            } />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
};

export const AppNavigator: React.FC = () => {
  const { state } = useApp();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleMenuClick = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  return (
    <Router>
      <ScrollToTop />
      <Routes>
        {/* Public homepage with public theme */}
        <Route 
          path="/" 
          element={
            <ThemeWrapper theme={publicTheme}>
              <CssBaseline />
              <PublicHomePage />
            </ThemeWrapper>
          } 
        />

        {/* FEAT-066 (MVP-18.1) — guest discovery grid without login */}
        <Route
          path="/discover"
          element={
            <ThemeWrapper theme={publicTheme}>
              <CssBaseline />
              <SearchCaretakersPage guestMode />
            </ThemeWrapper>
          }
        />
        
        {/* All other routes with default theme */}
        <Route 
          path="*" 
          element={
            <AuthenticatedApp 
              drawerOpen={drawerOpen}
              onMenuClick={handleMenuClick}
              onDrawerClose={handleDrawerClose}
            />
          } 
        />
      </Routes>
    </Router>
  );
};